import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, Dialog, IconButton, MenuItem, TextField, Checkbox, Chip, LinearProgress, CircularProgress,
} from '@mui/material';
import * as XLSX from 'xlsx';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import http, { apiErrorMessage } from '../Api/http';
import { CreateDepartment } from '../Api/Api';

const PRIMARY = '#7C5CFC';
const SOFT = '#F1EEFE';
const SOFT_BORDER = '#C9BEFB';
const LINE = '#E6EAF1';
const field = {
    '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, bgcolor: '#fff', '& fieldset': { borderColor: '#E5E7EB' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 } },
};
const btnSolid = { height: 42, px: 2.6, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#fff', bgcolor: PRIMARY, boxShadow: `0 2px 6px ${PRIMARY}40`, '&:hover': { bgcolor: '#6246E0' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' } };
const btnGhost = { height: 42, px: 2.2, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#475569', border: '1px solid #E5E7EB', bgcolor: '#fff', '&:hover': { bgcolor: '#F8FAFC' } };

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
const clean = (s) => String(s ?? '').trim().replace(/\s+/g, ' ');
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const isDeptHeader = (h) => /depart|dept|team|function/.test(norm(h));
// Values that are clearly not departments (heading text repeated inside the data).
const JUNK = /^(employee name|department|designation|name|s\.? ?no\.?|-|na|n\/a)$/i;

// A short code for a department name: initials for several words (Social Media Marketing -> SMM),
// otherwise the first letters. Kept unique within the batch.
const makeCode = (name, taken) => {
    const words = clean(name).split(/[^A-Za-z0-9]+/).filter(Boolean);
    let base = (words.length > 1 ? words.map((w) => w[0]).join('') : (words[0] || 'DEP').slice(0, 4)).toUpperCase().slice(0, 6);
    if (!base) base = 'DEP';
    let code = base; let n = 2;
    while (taken.has(code)) { code = `${base}${n}`; n += 1; }
    taken.add(code);
    return code;
};

// Reads the Department column of a workbook and creates the chosen values as departments in the entity
// being worked in, so an HR workbook can seed Organisation in one go.
export default function ImportDepartmentsDialog({ open, onClose, entityId, entityName, existingNames, onImported }) {
    const [file, setFile] = useState(null);
    const [sheets, setSheets] = useState([]); // { name, aoa, header, cols: [{ idx, label }] }
    const [sheetIdx, setSheetIdx] = useState(0);
    const [colIdx, setColIdx] = useState('');
    const [picked, setPicked] = useState({}); // value -> on
    const [parseError, setParseError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const input = useRef(null);

    useEffect(() => {
        if (open) { setFile(null); setSheets([]); setSheetIdx(0); setColIdx(''); setPicked({}); setParseError(''); setImporting(false); setProgress(0); setResult(null); }
    }, [open]);

    const existing = useMemo(() => new Set((existingNames || []).map(norm)), [existingNames]);
    const sheet = sheets[sheetIdx];

    const unique = useMemo(() => {
        if (!sheet || colIdx === '') return [];
        const counts = new Map();
        sheet.aoa.slice(sheet.header + 1).forEach((r) => {
            const v = clean(r[Number(colIdx)]);
            if (!v || JUNK.test(v)) return;
            const k = norm(v);
            const cur = counts.get(k);
            counts.set(k, cur ? { ...cur, count: cur.count + 1 } : { value: v, count: 1 });
        });
        return [...counts.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    }, [sheet, colIdx]);

    const choose = (list, i, col) => {
        const sh = list[i];
        if (!sh) return;
        setSheetIdx(i);
        const c = col ?? (sh.cols[0] ? String(sh.cols[0].idx) : '');
        setColIdx(c);
        const next = {};
        if (c !== '') {
            sh.aoa.slice(sh.header + 1).forEach((r) => { const v = clean(r[Number(c)]); if (v && !JUNK.test(v) && !existing.has(norm(v))) next[norm(v)] = true; });
        }
        setPicked(next);
    };

    const readFile = async (f) => {
        if (!f) return;
        setParseError('');
        if (!/\.(csv|xlsx|xls)$/i.test(f.name)) { setParseError('Choose a .csv, .xlsx or .xls file.'); return; }
        try {
            const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
            const found = wb.SheetNames.map((name) => {
                const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', blankrows: false });
                let header = aoa.slice(0, 8).findIndex((r) => r.some((c) => isDeptHeader(c)));
                if (header < 0) return null;
                const cols = aoa[header].map((c, idx) => ({ idx, label: clean(c) })).filter((c) => c.label && isDeptHeader(c.label));
                return cols.length ? { name, aoa, header, cols } : null;
            }).filter(Boolean);
            if (found.length === 0) { setParseError('No sheet in this file has a Department column. A heading such as "Department" or "Team" is needed.'); return; }
            setFile(f); setSheets(found); choose(found, 0, undefined);
        } catch {
            setParseError('Could not read that file. Check it is a valid spreadsheet.');
        }
    };

    const chosen = unique.filter((u) => picked[norm(u.value)] && !existing.has(norm(u.value)));

    const run = async () => {
        setImporting(true); setProgress(0);
        const taken = new Set();
        const failed = [];
        let done = 0;
        for (const u of chosen) {
            try {
                const { data: body } = await http.post(CreateDepartment, {
                    entityId: String(entityId), name: u.value, code: makeCode(u.value, taken), description: '', isActive: true,
                });
                if (body?.error) throw new Error(body.message || 'Rejected');
                done += 1;
            } catch (err) {
                failed.push({ title: u.value, note: err?.response || err?.request ? apiErrorMessage(err, 'Could not create') : err.message });
            }
            setProgress(Math.round(((done + failed.length) / chosen.length) * 100));
        }
        setImporting(false);
        setResult({ added: done, failed });
        if (done > 0) onImported?.(done);
    };

    return (
        <Dialog
            open={open} onClose={() => !importing && onClose()} maxWidth={false}
            slotProps={{
                backdrop: { sx: { backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' } },
                paper: { sx: { width: 'min(760px, 96vw)', height: 'min(660px, 92vh)', m: 1, borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid ${LINE}`, boxShadow: '0 30px 70px -20px rgba(15,23,42,0.35)' } },
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${LINE}` }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 16px -6px rgba(124,92,252,0.6)' }}>
                        <CloudUploadRoundedIcon sx={{ color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>Import Departments</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>Added to {entityName || 'the selected entity'}</Typography>
                    </Box>
                </Stack>
                <IconButton onClick={onClose} disabled={importing} sx={{ bgcolor: '#F1F5F9' }}><CloseRoundedIcon sx={{ fontSize: 19 }} /></IconButton>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.4 }}>
                {result ? (
                    <Box>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A', mb: 2 }}>Import complete</Typography>
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                            {[['Created', result.added, '#16A34A'], ['Failed', result.failed.length, '#DC2626']].map(([k, v, c]) => (
                                <Box key={k} sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: '#FBFAFF', border: `1px solid ${LINE}`, borderTop: `3px solid ${c}` }}>
                                    <Typography sx={{ fontSize: 28, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#64748B', mt: 0.6 }}>{k}</Typography>
                                </Box>
                            ))}
                        </Stack>
                        {result.failed.map((r) => (
                            <Stack key={r.title} direction="row" sx={{ justifyContent: 'space-between', gap: 2, py: 1, borderBottom: `1px solid ${LINE}` }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{r.title}</Typography>
                                <Typography sx={{ fontSize: 12.5, color: '#DC2626', textAlign: 'right' }}>{r.note}</Typography>
                            </Stack>
                        ))}
                    </Box>
                ) : !file ? (
                    <Box>
                        <input ref={input} type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                        <Box
                            onClick={() => input.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
                            sx={{ height: 290, borderRadius: '16px', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `2px dashed ${dragOver ? PRIMARY : SOFT_BORDER}`, bgcolor: dragOver ? SOFT : '#FBFAFF', transition: 'all .18s', '&:hover': { borderColor: PRIMARY, bgcolor: SOFT } }}
                        >
                            <Box sx={{ width: 76, height: 76, borderRadius: '20px', bgcolor: PRIMARY, boxShadow: '0 10px 24px -10px rgba(124,92,252,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.8 }}>
                                <CloudUploadRoundedIcon sx={{ fontSize: 36, color: '#fff' }} />
                            </Box>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>Drag a file here, or click to browse</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#64748B', mt: 0.4 }}>.xlsx, .xls or .csv · reads the Department column</Typography>
                        </Box>
                        {parseError && <Typography sx={{ fontSize: 12.5, color: '#DC2626', mt: 1.4, fontWeight: 600 }}>{parseError}</Typography>}
                    </Box>
                ) : (
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.8 }}>
                            <DescriptionRoundedIcon sx={{ fontSize: 18, color: PRIMARY }} />
                            <Typography noWrap sx={{ fontSize: 12.5, color: '#64748B', flex: 1 }}>{file.name} · {fmtSize(file.size)}</Typography>
                            <Button size="small" onClick={() => input.current?.click()} sx={{ textTransform: 'none', fontWeight: 700, color: PRIMARY }}>Change</Button>
                            <input ref={input} type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A', mb: 0.7 }}>Sheet <Box component="span" sx={{ color: '#64748B', fontWeight: 500 }}>· {sheets.length} with a department column</Box></Typography>
                                <TextField select fullWidth size="small" value={sheetIdx} onChange={(e) => choose(sheets, Number(e.target.value), undefined)} sx={field}>
                                    {sheets.map((sh, i) => <MenuItem key={sh.name} value={i} sx={{ fontSize: 13 }}>{sh.name.trim()}</MenuItem>)}
                                </TextField>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A', mb: 0.7 }}>Column</Typography>
                                <TextField select fullWidth size="small" value={colIdx} onChange={(e) => choose(sheets, sheetIdx, e.target.value)} sx={field}>
                                    {(sheet?.cols || []).map((c) => <MenuItem key={c.idx} value={String(c.idx)} sx={{ fontSize: 13 }}>{c.label}</MenuItem>)}
                                </TextField>
                            </Box>
                        </Stack>

                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: '#64748B' }}>{unique.length} DIFFERENT VALUES FOUND</Typography>
                            <Button size="small" onClick={() => { const all = unique.every((u) => existing.has(norm(u.value)) || picked[norm(u.value)]); const next = {}; if (!all) unique.forEach((u) => { if (!existing.has(norm(u.value))) next[norm(u.value)] = true; }); setPicked(next); }} sx={{ textTransform: 'none', fontWeight: 700, color: PRIMARY }}>
                                {unique.every((u) => existing.has(norm(u.value)) || picked[norm(u.value)]) ? 'Untick all' : 'Tick all'}
                            </Button>
                        </Stack>
                        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '12px', overflow: 'hidden' }}>
                            {unique.map((u, i) => {
                                const exists = existing.has(norm(u.value));
                                return (
                                    <Stack key={norm(u.value)} direction="row" sx={{ alignItems: 'center', px: 1.2, py: 0.3, borderTop: i ? `1px solid ${LINE}` : 'none', opacity: exists ? 0.6 : 1 }}>
                                        <Checkbox size="small" checked={Boolean(picked[norm(u.value)])} disabled={exists} onChange={(e) => setPicked((p) => ({ ...p, [norm(u.value)]: e.target.checked }))} sx={{ color: SOFT_BORDER, '&.Mui-checked': { color: PRIMARY } }} />
                                        <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: '#0F172A' }}>{u.value}</Typography>
                                        {exists ? <Chip size="small" label="Already exists" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#FEF3C7', color: '#B45309' }} /> : <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{u.count} row{u.count === 1 ? '' : 's'}</Typography>}
                                    </Stack>
                                );
                            })}
                            {unique.length === 0 && <Typography sx={{ p: 2, fontSize: 13, color: '#98A0AE' }}>This column has no values.</Typography>}
                        </Box>
                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE', mt: 1.2 }}>
                            Untick anything that is not a department (a person&apos;s name, a heading). Each department gets a short code made from its name.
                        </Typography>
                        {importing && (
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', mb: 0.6 }}>Creating departments… {progress}%</Typography>
                                <LinearProgress variant="determinate" value={progress} sx={{ height: 7, borderRadius: 5, bgcolor: '#EEF1F6', '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, px: 3, py: 1.8, borderTop: `1px solid ${LINE}`, bgcolor: '#FBFAFE' }}>
                {result ? (
                    <Button onClick={onClose} sx={btnSolid}>Done</Button>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={importing} sx={btnGhost}>Cancel</Button>
                        {file && (
                            <Button onClick={run} disabled={chosen.length === 0 || importing} endIcon={importing ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={btnSolid}>
                                {importing ? 'Creating…' : `Create ${chosen.length} department${chosen.length === 1 ? '' : 's'}`}
                            </Button>
                        )}
                    </>
                )}
            </Stack>
        </Dialog>
    );
}
